import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const TIPOS = [
  { v: "FACTURA", label: "Factura" },
  { v: "BOLETA", label: "Boleta" },
  { v: "RECIBO_HONORARIOS", label: "Recibo por Honorarios" },
  { v: "NOTA_CREDITO", label: "Nota de Crédito" },
  { v: "NOTA_DEBITO", label: "Nota de Débito" },
];

const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Compras() {
  const { empresa, formatApiError } = useApp();
  const [items, setItems] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [open, setOpen] = useState(false);
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");

  const blankItem = { producto_id: "", codigo: "", descripcion: "", unidad: "NIU", cantidad: 1, precio_unitario: 0, descuento: 0, afecto_igv: true };
  const blank = {
    tipo: "FACTURA", serie: "F001", numero: "",
    fecha_emision: new Date().toISOString().slice(0, 10),
    proveedor_id: "", moneda: "PEN", tipo_cambio: 1, detraccion: 0,
    items: [{ ...blankItem }], observaciones: "",
  };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const p = new URLSearchParams();
    if (filtroDesde) p.set("desde", filtroDesde);
    if (filtroHasta) p.set("hasta", filtroHasta);
    const { data } = await api.get(`/compras?${p}`);
    setItems(data);
  };
  useEffect(() => { if (empresa) { load(); api.get("/proveedores").then((r) => setProveedores(r.data)); api.get("/productos").then((r) => setProductos(r.data)); } /* eslint-disable-next-line */ }, [empresa, filtroDesde, filtroHasta]);

  const setItem = (idx, field, value) => setForm((f) => { const a = [...f.items]; a[idx] = { ...a[idx], [field]: value }; return { ...f, items: a }; });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...blankItem }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickProducto = (idx, pid) => {
    const p = productos.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => { const a = [...f.items]; a[idx] = { ...a[idx], producto_id: p.id, codigo: p.codigo, descripcion: p.descripcion, unidad: p.unidad, precio_unitario: p.precio_compra, afecto_igv: p.afecto_igv }; return { ...f, items: a }; });
  };

  const totales = useMemo(() => {
    let sg = 0, si = 0;
    form.items.forEach((it) => { const n = (+it.cantidad || 0) * (+it.precio_unitario || 0) - (+it.descuento || 0); it.afecto_igv ? sg += n : si += n; });
    const igv = sg * 0.18;
    return { sg, si, igv, total: sg + si + igv };
  }, [form.items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.proveedor_id) { toast.error("Selecciona un proveedor"); return; }
    try {
      const payload = { ...form, detraccion: +form.detraccion, items: form.items.map((it) => ({ ...it, cantidad: +it.cantidad, precio_unitario: +it.precio_unitario, descuento: +it.descuento })) };
      await api.post("/compras", payload);
      toast.success("Compra registrada"); setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const eliminar = async (c) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/compras/${c.id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Compras</h1>
          <p className="text-[13px] text-slate-500">Registro de comprobantes de compra para crédito fiscal y gastos.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(blank); }}>
          <DialogTrigger asChild><Button data-testid="btn-nueva-compra" className="h-9 bg-slate-900 hover:bg-slate-800 text-white"><Plus className="w-4 h-4 mr-1" /> Nueva compra</Button></DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Registro de compra</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 text-sm">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2">
                  <Label className="text-[12px]">Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">Serie</Label>
                  <Input value={form.serie} onChange={(e) => setForm({ ...form, serie: e.target.value })} className="h-9 mono" />
                </div>
                <div>
                  <Label className="text-[12px]">Número</Label>
                  <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} className="h-9 mono" />
                </div>
                <div>
                  <Label className="text-[12px]">Fecha</Label>
                  <Input type="date" value={form.fecha_emision} onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })} className="h-9" />
                </div>
                <div>
                  <Label className="text-[12px]">Moneda</Label>
                  <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="PEN">PEN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-[12px]">Proveedor</Label>
                  <Select value={form.proveedor_id} onValueChange={(v) => setForm({ ...form, proveedor_id: v })}>
                    <SelectTrigger data-testid="compra-prov" className="h-9"><SelectValue placeholder="Selecciona proveedor" /></SelectTrigger>
                    <SelectContent>{proveedores.map((p) => <SelectItem key={p.id} value={p.id}><span className="mono mr-2">{p.numero_doc}</span>{p.razon_social}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">Detracción S/</Label>
                  <Input type="number" step="0.01" value={form.detraccion} onChange={(e) => setForm({ ...form, detraccion: e.target.value })} className="h-9 tabular text-right" />
                </div>
                <div>
                  <Label className="text-[12px]">T.C.</Label>
                  <Input type="number" step="0.001" value={form.tipo_cambio} onChange={(e) => setForm({ ...form, tipo_cambio: e.target.value })} className="h-9 tabular text-right" />
                </div>
              </div>

              <div className="border border-slate-200 rounded">
                <table className="tbl w-full">
                  <thead><tr><th>Producto</th><th>Descripción</th><th>UM</th><th className="text-right">Cant.</th><th className="text-right">P.U.</th><th>IGV</th><th className="text-right">Subtotal</th><th></th></tr></thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx}>
                        <td className="w-48">
                          <Select value={it.producto_id || "none"} onValueChange={(v) => v !== "none" && pickProducto(idx, v)}>
                            <SelectTrigger className="h-8 text-[12px]"><SelectValue placeholder="Producto" /></SelectTrigger>
                            <SelectContent>{productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.codigo} - {p.descripcion}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><Input value={it.descripcion} onChange={(e) => setItem(idx, "descripcion", e.target.value)} className="h-8 text-[12px]" /></td>
                        <td className="w-16"><Input value={it.unidad} onChange={(e) => setItem(idx, "unidad", e.target.value)} className="h-8 mono text-[12px]" /></td>
                        <td className="w-20"><Input type="number" step="0.01" value={it.cantidad} onChange={(e) => setItem(idx, "cantidad", e.target.value)} className="h-8 tabular text-right text-[12px]" /></td>
                        <td className="w-24"><Input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => setItem(idx, "precio_unitario", e.target.value)} className="h-8 tabular text-right text-[12px]" /></td>
                        <td className="text-center"><input type="checkbox" checked={it.afecto_igv} onChange={(e) => setItem(idx, "afecto_igv", e.target.checked)} /></td>
                        <td className="tabular text-right font-medium">{fmt((it.cantidad * it.precio_unitario) - (+it.descuento || 0))}</td>
                        <td><button type="button" onClick={() => removeItem(idx)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 border-t border-slate-200">
                  <Button type="button" onClick={addItem} variant="outline" size="sm" className="h-7 text-[12px]"><Plus className="w-3 h-3 mr-1" /> Agregar línea</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[12px]">Observaciones</Label>
                  <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className="h-9" />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[12px] tabular">
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">Sub. gravado</span><span className="font-medium">S/ {fmt(totales.sg)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">Inafecto</span><span>S/ {fmt(totales.si)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">IGV (18%)</span><span className="font-medium">S/ {fmt(totales.igv)}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-slate-600">Detracción</span><span>- S/ {fmt(form.detraccion)}</span></div>
                  <div className="flex justify-between pt-2 mt-1 border-t border-slate-300 text-sm"><span className="font-bold">TOTAL</span><span className="font-bold">S/ {fmt(totales.total)}</span></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="compra-submit" type="submit" className="bg-slate-900 text-white">Registrar compra</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200 flex gap-3">
          <Input type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} className="h-9 w-40 text-[12px]" />
          <Input type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} className="h-9 w-40 text-[12px]" />
        </div>
        <table className="tbl w-full">
          <thead><tr><th>Fecha</th><th>Tipo</th><th>Comprobante</th><th>Proveedor</th><th className="text-right">Base</th><th className="text-right">IGV</th><th className="text-right">Total</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-500">Sin compras en el periodo</td></tr>
              : items.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.fecha_emision?.slice(0, 10)}</td>
                  <td><span className="badge badge-muted">{c.tipo.replace("_", " ")}</span></td>
                  <td className="mono font-medium">{c.serie}-{c.numero}</td>
                  <td>
                    <div className="font-medium text-slate-800">{c.proveedor?.razon_social}</div>
                    <div className="text-[10px] mono text-slate-500">{c.proveedor?.numero_doc}</div>
                  </td>
                  <td className="tabular text-right">{fmt(c.subtotal_gravado)}</td>
                  <td className="tabular text-right">{fmt(c.igv)}</td>
                  <td className="tabular text-right font-bold">{fmt(c.total)}</td>
                  <td className="text-right"><button onClick={() => eliminar(c)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
